import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";

export default function Hero() {
    return (
        <section
            data-testid="hero-section"
            className="relative bg-white overflow-hidden"
        >
            {/* Full-width editorial banner */}
            <div
                data-testid="hero-image-frame"
                className="relative w-full h-[45vh] min-h-[320px] lg:h-[55vh] lg:min-h-[440px] overflow-hidden bg-acre-bg"
            >
                <img
                    src="https://images.unsplash.com/photo-1633630663326-2b48b25c6fb2?w=2400&q=80&auto=format&fit=crop"
                    alt="A red family-farm barn set against a lush green pasture and rolling landscape"
                    data-testid="hero-image"
                    className="w-full h-full object-cover object-[50%_78%]"
                    loading="eager"
                />
                {/* Warmth vignette */}
                <div
                    aria-hidden="true"
                    className="absolute inset-0 pointer-events-none"
                    style={{
                        background:
                            "radial-gradient(ellipse at 40% 45%, rgba(0,0,0,0) 55%, rgba(0,0,0,0.32) 100%)"
                    }}
                />
                {/* Grain overlay */}
                <div
                    aria-hidden="true"
                    className="absolute inset-0 pointer-events-none mix-blend-multiply opacity-[0.08]"
                    style={{
                        backgroundImage:
                            "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='120' height='120'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2' stitchTiles='stitch'/></filter><rect width='100%' height='100%' filter='url(%23n)'/></svg>\")"
                    }}
                />
            </div>

            {/* Text block below the banner */}
            <div className="max-w-[1400px] mx-auto px-6 md:px-10 lg:px-16 pt-16 lg:pt-24 pb-20 lg:pb-28">
                <div className="flex items-center gap-4 mb-8 reveal">
                    <span className="acre-tick" />
                    <span className="text-[11px] uppercase tracking-[0.28em] text-acre-muted">
                        Chesterton
                    </span>
                </div>

                <h1
                    data-testid="hero-heading"
                    className="font-serif-display text-4xl sm:text-5xl lg:text-6xl xl:text-[64px] leading-[1.05] tracking-tight text-acre-secondary reveal reveal-delay-1"
                >
                    <em>Pietas et Dispensatio.</em>
                </h1>
                <div
                    data-testid="hero-subtitle"
                    className="mt-3 lg:mt-4 font-serif-display italic text-xl lg:text-2xl text-acre-muted reveal reveal-delay-1"
                >
                    Piety and stewardship.
                </div>

                <div className="mt-8 lg:mt-10 grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-16 items-start reveal reveal-delay-2">
                    <div className="lg:col-span-7 space-y-5">
                        <p className="text-acre-muted text-base lg:text-lg leading-[1.7]">
                            Familial piety is the honor and provision we owe those who came before us, and those who come after. Stewardship is the recognition that what we hold has been entrusted, not simply earned.
                        </p>
                        <p className="text-acre-muted text-base lg:text-lg leading-[1.7]">
                            Our clients are families with land to hold, businesses to hand on, and estates worth planning carefully. We handle every matter from first conversation through final signature, in plain language and with the deliberateness that generational decisions deserve.
                        </p>
                    </div>

                    <div className="lg:col-span-5 lg:pl-8 lg:border-l lg:border-acre-line flex flex-col items-start gap-5">
                        <Link
                            to="/contact"
                            data-testid="hero-cta-primary"
                            className="group inline-flex items-center gap-3 bg-acre-primary text-white px-7 py-4 text-[11px] uppercase tracking-[0.22em] font-semibold hover:bg-acre-primaryHover transition-all"
                        >
                            Contact the Firm
                            <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform" />
                        </Link>
                        <Link
                            to="/practice-areas"
                            data-testid="hero-cta-secondary"
                            className="inline-flex items-center gap-2 text-acre-ink text-[11px] uppercase tracking-[0.22em] font-semibold border-b border-acre-secondary pb-1 hover:text-acre-secondary transition-colors"
                        >
                            View Practice Areas
                        </Link>
                    </div>
                </div>
            </div>
        </section>
    );
}
